import { Badge, Text, Tooltip } from "@mantine/core";
import { IconMailForward, IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useParams } from "react-router-dom";

import {
  useGetWorkspaceInvitations,
  useRevokeWorkspaceInvitation,
  useSendWorkspaceInvitation,
} from "../../../api/workspace";
import { useConfirm } from "../../../hooks/useConfirm";
import { DataTable } from "../../DataTable";
import { TableAction, TableActions, actionsColumn } from "../../TableActions";
import { SectionLabel } from "./SectionLabel";

dayjs.extend(relativeTime);

// `not_sent` is an invitation a sysAdmin recorded without emailing it; its
// link only starts to exist (and to expire) once someone sends it.
const STATUS = {
  pending: { label: "pending", color: "yellow" },
  not_sent: { label: "not sent", color: "blue" },
  expired: { label: "expired", color: "gray" },
};

function RevokeInvitationButton({ workspaceId, invitationId }) {
  const { mutate } = useRevokeWorkspaceInvitation({
    workspaceId,
    invitationId,
  });
  const confirm = useConfirm();

  const openModal = () =>
    confirm({
      title: "Revoke invitation",
      message:
        "Revoke this invitation? The link in the email will stop working.",
      confirmLabel: "Revoke",
      confirmColor: "red",
      onConfirm: () => mutate({}),
    });

  return (
    <TableAction
      label="Revoke invitation"
      icon={IconTrash}
      color="red"
      onClick={openModal}
    />
  );
}

// Sends the accept link for a "not sent" invitation, or a fresh one for an
// expired invitation. The confirm is where the 7-day clock is explained,
// since the table shows no expiry until the email is out.
function SendInvitationButton({ workspaceId, invitation }) {
  const { mutate } = useSendWorkspaceInvitation({
    workspaceId,
    invitationId: invitation.id,
  });
  const confirm = useConfirm();

  const isResend = invitation.status === "expired";

  const openModal = () =>
    confirm({
      title: isResend ? "Send a new invitation link" : "Send invitation email",
      message: `${isResend ? "Email a new invitation link" : "Email the invitation"} to ${invitation.email}? The link will expire 7 days after sending.`,
      confirmLabel: "Send email",
      onConfirm: () => mutate({}),
    });

  return (
    <TableAction
      label={isResend ? "Send a new invitation link" : "Send invitation email"}
      icon={IconMailForward}
      color="blue"
      onClick={openModal}
    />
  );
}

const HOUR_MS = 60 * 60 * 1000;

// "in 6 days" / "3 days ago" reads faster than a date; the exact moment is
// one hover away. Never finer than an hour: "in 12 minutes" is false
// precision for a link that lasts a week, and it would go stale while the
// page sits open.
function describeExpiry(invitation) {
  const expiresAt = dayjs(invitation.expiresAt);
  const expired = invitation.status === "expired";

  if (Math.abs(expiresAt.diff(dayjs())) < HOUR_MS) {
    return expired
      ? "Expired less than an hour ago"
      : "Expires in less than an hour";
  }

  return expired
    ? `Expired ${expiresAt.fromNow()}`
    : `Expires ${expiresAt.fromNow()}`;
}

function ExpiryCell({ invitation }) {
  if (!invitation.expiresAt) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Tooltip label={dayjs(invitation.expiresAt).format("DD MMM YYYY, HH:mm")}>
      <Text
        size="sm"
        c={invitation.status === "expired" ? "dimmed" : undefined}
      >
        {describeExpiry(invitation)}
      </Text>
    </Tooltip>
  );
}

export const PendingInvitationsTable = ({ canManageMembers }) => {
  const { id } = useParams();
  const { data: invitations, isPending } = useGetWorkspaceInvitations(id);

  const columns = [
    { accessor: "email" },
    {
      accessor: "status",
      // Keeps the badge from being squeezed when the table is narrow: the
      // table scrolls instead.
      noWrap: true,
      render: (row) => {
        const status = STATUS[row.status] ?? {
          label: row.status,
          color: "gray",
        };
        return <Badge color={status.color}>{status.label}</Badge>;
      },
    },
    {
      accessor: "expiresAt",
      title: "Expires",
      noWrap: true,
      render: (row) => <ExpiryCell invitation={row} />,
    },
  ];

  if (canManageMembers) {
    // Anything not yet accepted can be revoked; anything without a live link
    // (never sent, or expired) can be sent.
    columns.push(
      actionsColumn((row) => (
        <TableActions>
          {(row.status === "not_sent" || row.status === "expired") && (
            <SendInvitationButton workspaceId={id} invitation={row} />
          )}
          {(row.status === "pending" || row.status === "not_sent") && (
            <RevokeInvitationButton workspaceId={id} invitationId={row.id} />
          )}
        </TableActions>
      ))
    );
  }

  const isEmpty = !isPending && (!invitations || invitations.length === 0);

  return (
    <>
      <SectionLabel
        title="Pending invitations"
        count={isPending ? undefined : (invitations?.length ?? 0)}
      />
      {isEmpty ? (
        <Text c="dimmed" size="sm">
          No pending invitations.
        </Text>
      ) : (
        <DataTable
          records={invitations}
          columns={columns}
          fetching={isPending}
          withColumnBorders={false}
          minHeight={120}
          verticalSpacing="sm"
        />
      )}
    </>
  );
};
